import { Button, Checkbox, DialogV2, TextArea, Typography } from '@recruit-robin/robin-theme/web-components';
import { StatusChangeFeedbackParams } from 'clients/CampaignsClient/CampaignsClient.types';
import { memo, useContext, useEffect, useState } from 'react';

import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { GlobalDialogType } from 'stores/global-dialogs/global-dialogs.store.types';
import { useGlobalDialog } from 'stores/global-dialogs/hooks/useGlobalDialog';
import { useGlobalDialogData } from 'stores/global-dialogs/hooks/useGlobalDialogData';
import { useIsGlobalDialogOpen } from 'stores/global-dialogs/hooks/useIsGlobalDialogOpen';
import { useStyles } from './CandidateFeedbackModal.styles';

export const CandidateFeedbackModal = memo(() => {
  const candidateFeedbackModal = useGlobalDialog(GlobalDialogType.CandidateFeedback);
  const isVisible = useIsGlobalDialogOpen(GlobalDialogType.CandidateFeedback);
  const dialogData = useGlobalDialogData({ dialogType: GlobalDialogType.CandidateFeedback, isVisible });
  const classes = useStyles();
  const { dictionary } = useContext(LocalizationContext);

  const [selectedFeedbackReasons, setSelectedFeedbackReasons] = useState<string[]>([]);
  const [additionalDetails, setAdditionalDetails] = useState('');

  useEffect(() => {
    if (!isVisible) {
      setSelectedFeedbackReasons([]);
      setAdditionalDetails('');
    }
  }, [isVisible]);

  const feedbackOptions = [
    {
      key: 'IRRELEVANT_EXPERIENCE',
      value: dictionary.hideCandidateFeedbackReasonIrrelevantExperience,
    },
    {
      key: 'MISMATCHED_PREFERENCES',
      value: dictionary.hideCandidateFeedbackReasonMismatchedPreferences,
    },
    { key: 'UNSUITABLE_LOCATION', value: dictionary.hideCandidateFeedbackReasonLocation },
    {
      key: 'INSUFFICIENT_QUALIFICATIONS',
      value: dictionary.hideCandidateFeedbackReasonQualifications,
    },
    { key: 'LOW_QUALITY_PROFILE', value: dictionary.hideCandidateFeedbackReasonLowQuality },
  ];

  if (!dialogData) {
    return null;
  }

  const handleClose = () => {
    candidateFeedbackModal.close();
  };

  const handleFeedbackReasonChange = (feedbackReasonKey: string, checked: boolean) => {
    if (checked) {
      setSelectedFeedbackReasons((prev) => [...prev, feedbackReasonKey]);
    } else {
      setSelectedFeedbackReasons((prev) => prev.filter((key) => key !== feedbackReasonKey));
    }
  };

  const handleSubmit = () => {
    if (!dialogData?.onSubmit) {
      handleClose();

      return;
    }

    const feedback: StatusChangeFeedbackParams = selectedFeedbackReasons.map((feedbackReasonKey) => {
      const option = feedbackOptions.find((opt) => opt.key === feedbackReasonKey);

      return { key: option?.key || '', value: option?.value || '' };
    });

    if (additionalDetails.trim()) {
      feedback.push({ key: 'ADDITIONAL_DETAILS', value: additionalDetails.trim() });
    }

    dialogData.onSubmit(feedback.length > 0 ? feedback : undefined);

    handleClose();
  };

  return (
    <DialogV2.Root isVisible={isVisible} customCss={{ zIndex: 3 }}>
      <DialogV2.Backdrop />

      <DialogV2.Content customCss={classes.modalContent}>
        <DialogV2.Body customCss={classes.body}>
          <div css={classes.header}>
            <Typography variant={(v) => v.heading[3]} color={(c) => c.neutral.variant[80]}>
              {dictionary.hideCandidateFeedbackTitle}
            </Typography>
            <Typography variant={(v) => v.body.short} color={(c) => c.neutral.variant[80]}>
              {dictionary.hideCandidateFeedbackDescription}
            </Typography>
          </div>

          <div css={classes.checkboxGroup}>
            {feedbackOptions.map((option) => (
              <Checkbox
                key={option.key}
                label={option.value}
                customCss={classes.checkbox}
                checked={selectedFeedbackReasons.includes(option.key)}
                onClick={(checked) => handleFeedbackReasonChange(option.key, checked)}
              />
            ))}
          </div>

          <TextArea
            placeholder={dictionary.hideCandidateFeedbackDetailsPlaceholder}
            value={additionalDetails}
            onChange={(e) => setAdditionalDetails(e.target.value)}
            customCss={classes.textarea}
            rows={3}
            variant="gray"
            disableResize
          />

          <div css={classes.actions}>
            <Button
              style="filled"
              type="highlight"
              onClick={handleSubmit}
              label={dictionary.submit}
              disabled={selectedFeedbackReasons.length === 0 && !additionalDetails.trim()}
            />
          </div>
        </DialogV2.Body>
      </DialogV2.Content>
    </DialogV2.Root>
  );
});
